<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-full"
    title="個別サービス"
  >
    <template v-if="isLoaded" v-slot:body="{ cancel }">
      <v-card-text>
        <div class="modal-body">
          <v-container fluid class="pa-0">
            <v-row no-gutters>
              <v-col>
                <app-search :search="service.search" v-model="service.filter" />
              </v-col>
              <div class="d-flex flex-row ga-1 flex-wrap ml-2">
                <v-btn v-permission.config color="primary" @click="showRegister"
                  >追加
                </v-btn>
                <v-btn
                  v-permission.config
                  color="primary"
                  @click="deleteService"
                  >削除
                </v-btn>
              </div>
            </v-row>
            <v-alert v-if="!!alertMsg" class="my-2" :text="alertMsg" />
            <app-table
              :sort-null-last="true"
              :fields="service.fields"
              :items="service.items"
              :filter="service.filter"
              :filter-option="service.filter_option"
              :selectable="selectable"
              v-model:table-sort-by="service.sortBy"
              v-model="service.selected"
              primary-key="customServiceSeq"
              :hover="true"
              @click:row="showReference"
              :td-class="cellClass"
            >
              <template #cell(_selected)="data">
                <v-checkbox-btn
                  v-permission.config
                  v-if="!data.item.isPolicyInUse"
                  :value="data.item"
                  v-model="service.selected"
                  @click.stop
                />
              </template>
              <template #cell(description)="data">
                {{ $filter.emptyTo(data.value, "-") }}
              </template>
              <template #cell(isPolicyInUse)="data">
                {{ $filter.enumTo(data.value, "isPolicyInUse") }}
              </template>
            </app-table>
          </v-container>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-list-item class="w-100 pa-1">
          <template v-slot:append>
            <v-btn @click="cancel" variant="outlined" color="primary"
              >キャンセル
            </v-btn>
          </template>
        </v-list-item>
      </v-card-actions>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import type { CustomServiceIntranet } from "@/apis/CustomServiceApi";
import type {
  AppTableHeader,
  FilterOption,
  SortItem,
} from "@/components/AppTable.vue";
import { VueFinalModal } from "vue-final-modal";
import ServiceIntranetFwReference from "@/modals/applianceObject/ServiceIntranetFwReference.vue";
import ServiceIntranetFwEdit from "@/modals/applianceObject/ServiceIntranetFwEdit.vue";
import type { SearchOption } from "@/components/AppSearch.vue";

export default defineComponent({
  name: "IntranetFwCustomServiceList",
  props: {
    /**
     * ポリシー種別
     * INTRANET_FW ：イントラネットFW
     * INTRANET_FW_PFGW ：イントラネットFW（PFGW）
     */
    policyType: {
      type: String as PropType<"INTRANET_FW" | "INTRANET_FW_PFGW">,
      required: true,
    },
    /**
     * E番号(ACT)
     * ポリシー種別でINTRANET_FW_PFGWを指定時必須
     */
    enumberAct: {
      type: String as PropType<string>,
      default: undefined,
    },
  },
  data() {
    return {
      service: {
        sortBy: {
          key: "serviceName",
          order: "asc",
        } as SortItem,
        fields: [
          { key: "serviceName", title: "名前", sortable: true },
          {
            key: "protocol",
            title: "プロトコル",
            sortable: true,
            width: "160px",
          },
          {
            key: "protocolInfo",
            title: "Port/プロトコル番号/Type(Code)",
            sortable: true,
            formatter: (_: string, __: string, item: CustomServiceIntranet) => {
              if (item.protocol === "IP") {
                // プロトコルがIPの場合にはプロトコル番号を表示
                return item.protocolNumber;
              } else if (item.protocol === "ICMP") {
                // プロトコルがICMPの場合にはICMPタイプ,ICMPコードの形式で表示
                return `${item.icmpType}(${item.icmpCode})`;
              } else {
                // プロトコルがTCPかUDPの場合にはポート番号を表示
                return item.portNumber;
              }
            },
            sortByFormatted: true,
            filterByFormatted: true,
          },
          {
            key: "description",
            title: "備考",
            sortable: true,
            width: "200px",
          },
          {
            key: "isPolicyInUse",
            title: "ポリシー使用有無",
            sortable: true,
            width: "170px",
          },
        ] as AppTableHeader<CustomServiceIntranet>[],
        items: [] as CustomServiceIntranet[],
        selected: [] as CustomServiceIntranet[],
        search: {
          serviceName: { type: "text", label: "名前" },
          protocolInfo: {
            type: "text",
            label: "Port/プロトコル番号/Type(Code)",
          },
        } as SearchOption,
        filter: {} as Record<string, unknown>,
        filter_option: {
          serviceName: "like",
          protocolInfo: "like",
        } as FilterOption,
      },
      intraCustomServiceKeyId: "",
      alertMsg: "",
      isLoaded: false,
    };
  },
  computed: {
    /** true: 一覧選択可能, false: 不可 */
    selectable(): boolean {
      return this.$service.permission.hasAuthority("CONFIG");
    },
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    async load() {
      const res = await this.$api.customServiceApi.getIntranetfwCustomService(
        this.policyType,
        this.enumberAct
      );
      this.service.items = res.customServiceIntranetList;
      this.intraCustomServiceKeyId = res.customServiceKeyId;
    },

    /** サービス（イントラネットFW）設定登録モーダルを表示する */
    async showRegister() {
      this.alertMsg = "";
      await this.$modal.show(ServiceIntranetFwEdit, {
        policyType: this.policyType,
        enumberAct: this.enumberAct,
        customServiceKeyId: this.intraCustomServiceKeyId,
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },

    /**
     * サービス（イントラネットFW）設定参照モーダルを表示する
     * @param entity 選択した行のデータ
     */
    async showReference(entity: CustomServiceIntranet) {
      this.alertMsg = "";
      await this.$modal.show(ServiceIntranetFwReference, {
        policyType: this.policyType,
        enumberAct: this.enumberAct,
        customServiceIntranet: entity,
        customServiceKeyId: this.intraCustomServiceKeyId,
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },

    /** サービス（イントラネットFW）設定の一括削除処理 */
    async deleteService() {
      // チェックボックスが選択1つも選択されていない場合、アラート表示して処理終了
      if (this.service.selected.length === 0) {
        // アラートを表示する
        this.alertMsg = this.$filter.message("no_select_data");
        return;
      } else {
        // アラートを非表示にする
        this.alertMsg = "";
      }

      await this.$confirm(
        `個別サービスを${this.service.selected.length}件削除します。よろしいですか？`
      );
      await this.$api.customServiceApi.deleteIntranetfwCustomService(
        this.policyType,
        this.enumberAct,
        {
          customServiceSeqList: this.service.selected.map(
            (e) => e.customServiceSeq
          ),
          customServiceKeyId: this.intraCustomServiceKeyId,
        }
      );
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
    /** tdにクラスをつける */
    cellClass(field: AppTableHeader<unknown>): string[] | undefined {
      if (field.key === "description") {
        return ["text-pre-wrap"];
      }
      return undefined;
    },
  },
});
</script>
